import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from "react-i18next";

const LogViewerContainer = styled.div`
    display: flex;
    height: 100%;
    font-size: 0.8em; /* Same font size as chat messages */
    gap: 20px; /* Increase space between list and detail */
`;

const LogList = styled.div`
    width: 40%;
    border-right: 1px solid #ccc;
    overflow-y: auto;
    /* Increase padding on the right to push the content away from the vertical line */
    padding: 10px 20px 10px 10px;
`;

const LogDetail = styled.div`
    width: 60%;
    overflow-y: auto;
    padding: 10px;
`;

const LogItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 5px;
    border-bottom: 1px solid #ddd;
    cursor: pointer;
    position: relative;
    transition: background 0.2s;

    /* Highlight selected item */
    background: ${(props) => (props.selected ? '#dceeff' : 'transparent')}; /* Light blue background for selected item */
    &:hover {
        background: #f5f5f5;
    }
`;

const Column = styled.div`
    flex: 1;
    &:first-child {
        font-weight: bold;
    }
`;

const Tooltip = styled.div`
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: 5px;
    font-size: 0.7em;
    border-radius: 4px;
    z-index: 10;

    ${LogItem}:hover & {
        display: block;
    }
`;

const SortingControls = styled.div`
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SortButton = styled.button`
    padding: 4px 8px;
    font-size: 0.8em;
    background: #009fe3;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background: #007db7;
    }
`;

// Rating display container now set to inline-flex so stars appear inline.
const RatingDisplay = styled.div`
    display: inline-flex;
    gap: 2px;
    font-size: 1em;
    justify-content: flex-end; /* Align stars to the right */
    color: #FFD700;
`;

// Bordered container for detail information
const DetailBox = styled.div`
    border: 1px solid #ccc;
    padding: 10px;
    margin-bottom: 20px;
    border-radius: 8px;
    background: #fafafa;
`;

// Content wrapper that preserves HTML formatting (including list styles)
const DetailContent = styled.div`
    line-height: 1.5;
    & ul, & ol {
        margin: 10px 0;
        padding-left: 20px;
    }
    & li {
        margin-bottom: 5px;
    }
`;

// Helper: interpolate star color from red to green based on rating.
// Rating 1: red (rgb(255,0,0)); Rating 5: green (rgb(0,255,0))
const getStarColor = (rating) => {
    const norm = Math.max(0, Math.min((rating - 1) / 4, 1)); // normalized 0 to 1
    const r = Math.round(255 * (1 - norm));
    const g = Math.round(255 * norm);
    return `rgb(${r}, ${g}, 0)`;
};

// Helper function to render rating stars as individual spans.
const renderStars = (rating) => {
    const stars = [];
    const r = parseInt(rating, 10) || 0;
    for (let i = 1; i <= 5; i++) {
        if (i <= r) {
            stars.push(
                <span key={i} style={{ color: getStarColor(r) }}>
                    ★
                </span>
            );
        } else {
            stars.push(
                <span key={i} style={{ color: '#ccc' }}>
                    ☆
                </span>
            );
        }
    }
    return stars;
};

const LogViewer = () => {
    const [logs, setLogs] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [sortKey, setSortKey] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Use environment variable (or fallback) for logs endpoint.
    const LOGS_ENDPOINT = process.env.REACT_APP_LOGS_ENDPOINT || 'http://localhost:8080/logs';

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await axios.get(LOGS_ENDPOINT);
                setLogs(response.data);
            } catch (error) {
                console.error("Error fetching logs:", error);
            }
        };
        fetchLogs();
    }, [LOGS_ENDPOINT]);

    const sortedLogs = useMemo(() => {
        return [...logs].sort((a, b) => {
            let aVal = a[sortKey];
            let bVal = b[sortKey];
            if (sortKey === 'created_at') {
                aVal = new Date(aVal).getTime();
                bVal = new Date(bVal).getTime();
            }
            if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [logs, sortKey, sortOrder]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortOrder('desc');
        }
    };

    const { t, i18n } = useTranslation();

    return (
        <LogViewerContainer>
            <LogList>
                <SortingControls>
                    <SortButton onClick={() => handleSort('created_at')}>
                        {t('log.date') || 'Date'} {sortKey === 'created_at' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </SortButton>
                    <SortButton onClick={() => handleSort('rating')}>
                        {t('log.rating') || 'Rating'} {sortKey === 'rating' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </SortButton>
                </SortingControls>
                {sortedLogs.map((log) => (
                    <LogItem
                        key={log.p_id}
                        onClick={() => setSelectedLog(log)}
                        selected={selectedLog?.p_id === log.p_id}  /* Add this line to check if the log is selected */
                    >
                        <Column>{new Date(log.created_at).toLocaleString()}</Column>
                        <Column style={{ textAlign: 'right' }}>
                            <RatingDisplay>{renderStars(log.rating)}</RatingDisplay>
                        </Column>
                        <Tooltip>
                            {log.question ? log.question.substring(0, 100) + '...' : ''}
                        </Tooltip>
                    </LogItem>
                ))}
            </LogList>
            <LogDetail>
                {selectedLog ? (
                    <>
                        <h3>{t('log.details') || 'Log Details'}</h3>
                        <br />
                        <DetailBox>
                            <p><strong>p_id:</strong> {selectedLog.p_id}</p>
                            <p><strong>{t('log.date') || 'Date'}:</strong> {new Date(selectedLog.created_at).toLocaleString()}</p>
                            <p>
                                <strong>{t('log.rating') || 'Rating'}:</strong>{' '}
                                <span style={{ verticalAlign: 'middle', marginLeft: '5px' }}>
                                    {renderStars(selectedLog.rating)}
                                </span>
                            </p>
                        </DetailBox>
                        {selectedLog.reason && (
                            <>
                                <h3>{t('log.feedback') || 'Feedback'}</h3>
                                <br />
                                <DetailBox>
                                    <DetailContent>{selectedLog.reason}</DetailContent>
                                </DetailBox>
                            </>
                        )}
                        <h3>{t('log.question') || 'Question'}</h3>
                        <br />
                        <DetailBox>
                            <DetailContent dangerouslySetInnerHTML={{ __html: selectedLog.question }} />
                        </DetailBox>
                        <h3>{t('log.answer') || 'Answer'}</h3>
                        <br />
                        <DetailBox>
                            <DetailContent dangerouslySetInnerHTML={{ __html: selectedLog.answer }} />
                        </DetailBox>
                    </>
                ) : (
                    <p>{t('log.select') || 'Select a log entry to see details.'}</p>
                )}
            </LogDetail>
        </LogViewerContainer>
    );
};

export default LogViewer;
